import { reactMount } from '@/lib/reactMount'
import { PhoneIcon } from '@/components/Icons'
import { fetcher } from '@/lib/fetcher'
import { useState, useMemo, useEffect, useRef } from 'react'
import useSWRMutation from 'swr/mutation'
import { FormField } from '@/components/FormField'
import { Button } from '@/components/Button'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ErrorMessage } from '@/components/ErrorMessage'
import { SpecialistCard } from '@/components/SpecialistCard'
import { CallbackRequestDrawer } from '@/components/CallbackRequestDrawer'

export const FindSpecialistPage = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current

  const [selectedRegion, setSelectedRegion] = useState('')
  const [filterRegion, setFilterRegion] = useState('')
  const [callbackDrawerOpen, setCallbackDrawerOpen] = useState(false)
  const [selectedSpecialist, setSelectedSpecialist] = useState(null)

  const {
    trigger: triggerSearch,
    data: searchResults,
    error,
    isMutating: isLoading
  } = useSWRMutation('/api/specialist/find', fetcher)

  useEffect(() => {
    triggerSearch({ method: 'GET' })
  }, [triggerSearch])

  const specialists = searchResults?.data || []

  // Compute region list from Umbraco Locations (array of strings), sorted A→Z
  const regionOptions = useMemo(() => {
    const locations = Array.isArray(umbracoRef.current?.Locations) ? umbracoRef.current.Locations : []
    return locations.filter(Boolean).slice().sort((a, b) => a.localeCompare(b))
  }, [])

  const filteredSpecialists = useMemo(() => {
    if (!filterRegion) return specialists
    // Support both Regions (array) and region (string) for backward compatibility
    return specialists.filter(specialist =>
      (Array.isArray(specialist.Regions) && specialist.Regions.includes(filterRegion)) ||
      specialist.region === filterRegion
    )
  }, [filterRegion, specialists])

  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value)
  }

  const handleFindNow = () => {
    setFilterRegion(selectedRegion)
  }

  const handleRequestCallback = (specialist) => {
    setSelectedSpecialist(specialist)
    setCallbackDrawerOpen(true)
  }

  return (
    <>
      <section className='relative isolate py-24 md:px-4 lg:px-10 xl:px-24'>
        <header className='max-w-3xl text-center mx-auto mb-12'>
          {umbraco.Title &&
            <h1 className='text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl'>
              {umbraco.Title}
            </h1>}
          {umbraco.Summary &&
            <div className='_simple-rte mt-4 text-base text-balance font-semibold' dangerouslySetInnerHTML={{ __html: umbraco.Summary }} />}
        </header>

        <div className='mx-auto max-w-[1060px] px-4 xl:px-3 space-y-16'>
          <div className='grid grid-cols-1 gap-x-6 gap-y-10 md:gap-y-16 sm:grid-cols-2'>
            <aside className='relative overflow-hidden flex flex-col lg:flex-row lg:flex-wrap gap-3 px-6 py-8 lg:px-12 lg:py-16 rounded-2xl bg-primary-950'>
              <div className='absolute bg-yellow-rad w-full h-[600px] left-0 top-1/2 pointer-events-none' />
              <h3 className='relative text-2xl leading-7 font-extrabold text-white text-center lg:w-full'>Find a Specialist</h3>
              <div data-spacer className='lg:w-full' />
              <FormField
                type='select'
                label=''
                id='selectedRegion'
                name='selectedRegion'
                value={selectedRegion}
                onChange={handleRegionChange}
                options={regionOptions}
                placeholder='Select a region'
                disabled={isLoading}
                className='relative grow'
              />
              <Button
                size='lg'
                onClick={handleFindNow}
                className='relative'
              >
                Find Now
              </Button>
            </aside>

            <button
              className='relative flex flex-col px-6 py-8 items-start justify-between rounded-2xl overflow-hidden text-left h-[236px] sm:h-full'
              onClick={() => {
                setSelectedSpecialist(null)
                setCallbackDrawerOpen(true)
              }}
            >
              <img src='/card-img.png' className='absolute inset-0 w-full h-full object-cover' />
              <PhoneIcon className='relative size-8 text-white' />
              <span className='relative text-2xl leading-7 font-extrabold text-white text-balance'>Request a call from one of our lending specialists</span>
            </button>
          </div>

          <div className='mx-auto space-y-8'>
            {isLoading && <LoadingSpinner message='Loading specialists...' />}

            {!isLoading && !error && filteredSpecialists.length > 0 && (
              <div className='grid grid-cols-1 gap-x-8 gap-y-10 md:gap-y-16 sm:grid-cols-2 lg:grid-cols-3'>
                {filteredSpecialists.map((specialist, idx) => (
                  <SpecialistCard
                    key={`${specialist.name}-${specialist.phone || ''}-${idx}`}
                    specialist={specialist}
                    onRequestCallback={handleRequestCallback}
                  />
                ))}
              </div>
            )}

            {!isLoading && !error && filteredSpecialists.length === 0 && specialists.length > 0 && (
              <div className='text-center '>
                <p className='text-gray-500 text-lg'>
                  {filterRegion
                    ? `No specialists found in ${filterRegion}.`
                    : 'No specialists found in your selected region.'}
                </p>
              </div>
            )}

            {!isLoading && error && (
              <ErrorMessage message='Failed to load specialists. Please refresh the page.' />
            )}
          </div>
        </div>
      </section>

      <CallbackRequestDrawer
        open={callbackDrawerOpen}
        onClose={() => setCallbackDrawerOpen(false)}
        specialist={selectedSpecialist}
        regionOptions={regionOptions}
      />
    </>
  )
}

reactMount('FindSpecialistPage', FindSpecialistPage)
